import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "components/ui/dialog";
import { Loader } from "lucide-react";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import {
  element14ConfirmShippingAddress,
  element14ConfirmShippingMethod,
  element14OrderSubmit,
} from "actions/element14Action";
import { saveOrder } from "actions/cartActions";
import { warnIfHubSyncFailed } from "utils/hubSyncWarning";
import { useAuth } from "AuthContext";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { getErrorMessage } from "utils/helper";
import { runElement14Step } from "utils/element14Retry";
import SamiSpin from "components/SamiSpin";

const MySwal = withReactContent(Swal);

const getAddressLabel = (addr) => {
  if (!addr) return "";
  // Element14's own field names are nickName / organization / region.
  const parts = [
    addr.nickName,
    addr.organization || addr.companyName || addr.name,
    addr.addressLine1 || addr.address1,
    addr.city,
    addr.region || addr.stateOrProvince || addr.state,
    addr.postalCode || addr.postcode,
    addr.countryCode || addr.country,
  ].filter(Boolean);
  return parts.join(", ");
};

const getMethodLabel = (method) => {
  if (!method) return "";
  const name =
    method.shippingMethodName ||
    method.name ||
    method.description ||
    method.shippingMethodID;
  const cost =
    method.shippingMethodCost ?? method.cost ?? method.price;
  return cost !== undefined && cost !== null && cost !== ""
    ? `${name} (${cost})`
    : String(name);
};

const Element14Modal = ({ modelData, setModelData }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addressId, setAddressId] = useState("");
  const [shippingMethodId, setShippingMethodId] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [currency, setCurrency] = useState("USD");
  const { clearCartByPartner, cart } = useAuth();

  const shippingAddresses = Array.isArray(modelData?.shippingAddresses)
    ? modelData.shippingAddresses
    : [];
  const shippingMethods = Array.isArray(modelData?.shippingMethods)
    ? modelData.shippingMethods
    : [];
  const authToken = modelData?.element_14_auth_token;
  const dbOrderId = modelData?.dbOrderId;
  const element14OrderId = modelData?.element14OrderId;
  const cartDetailItems = modelData?.cartDetailItems || null;

  const handleClose = () => {
    setModelData({ ...modelData, open: false });
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!addressId || !shippingMethodId) {
      setError("Please select shipping address and shipping method.");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const selectedAddress = shippingAddresses.find(
        (a) => String(a.addressId ?? a.id) === String(addressId),
      );
      const selectedMethod = shippingMethods.find(
        (m) => String(m.shippingMethodID ?? m.id) === String(shippingMethodId),
      );

      // Carries the (possibly refreshed) auth token through each step. When a
      // step's Retry button is clicked, a fresh token is fetched and threaded
      // into the next call.
      let currentToken = authToken;

      const addressStep = await runElement14Step({
        token: currentToken,
        title: "Confirm Shipping Address Failed",
        fallbackMessage: "Failed to confirm shipping address.",
        resolveMessage: (res) => getErrorMessage(res?.data),
        apiCall: (t) =>
          element14ConfirmShippingAddress({
            addressId: selectedAddress?.addressId ?? selectedAddress?.id,
            addressDetails: selectedAddress,
            element_14_auth_token: t,
            orderId: dbOrderId,
          }),
      });
      if (!addressStep.ok) {
        setIsLoading(false);
        return;
      }
      currentToken = addressStep.token;

      const methodStep = await runElement14Step({
        token: currentToken,
        title: "Confirm Shipping Method Failed",
        fallbackMessage: "Failed to confirm shipping method.",
        resolveMessage: (res) => getErrorMessage(res?.data),
        apiCall: (t) =>
          element14ConfirmShippingMethod({
            shippingMethodID: selectedMethod?.shippingMethodID ?? selectedMethod?.id,
            shippingMethod: selectedMethod,
            element_14_auth_token: t,
            orderId: dbOrderId,
          }),
      });
      if (!methodStep.ok) {
        setIsLoading(false);
        return;
      }
      currentToken = methodStep.token;

      const submitStep = await runElement14Step({
        token: currentToken,
        title: "Order Submit Failed",
        fallbackMessage: "Failed to submit order.",
        resolveMessage: (res) => getErrorMessage(res?.data),
        apiCall: (t) =>
          element14OrderSubmit({
            element_14_auth_token: t,
            orderId: dbOrderId,
          }),
      });
      if (!submitStep.ok) {
        setIsLoading(false);
        return;
      }
      currentToken = submitStep.token;
      const submitRes = submitStep.res;

      const orderReference =
        submitRes?.data?.orderReference || submitRes?.data?.orderReferenceId;

      const element14CartItems = (cart || []).filter(
        (item) => String(item?.api_partner || "").toLowerCase() === "element14",
      );
      const bomLinks = element14CartItems
        .filter((item) => item?.bom_item_id)
        .map((item) => ({
          bom_item_id: item.bom_item_id,
          bom_id: item.bom_id ?? null,
          mfr_part_number: item.part_number ?? null,
          part_number: item.api_partner_part_number ?? null,
        }));

      const saveOrderResponse = await saveOrder({
        element14_data: {
          orderReference,
          element14OrderId,
          dbOrderId,
          shippingAddress: selectedAddress,
          shippingMethod: selectedMethod,
          poNumber: poNumber.trim() ? poNumber.trim() : `PO-${Date.now()}`,
          currency,
          submitResponse: submitRes?.data,
          cartDetailItems: element14CartItems,
        },
        bom_links: bomLinks,
      });
      warnIfHubSyncFailed(saveOrderResponse?.data?.hub_sync);

      clearCartByPartner("Element14");
      setModelData({ ...modelData, open: false });
      setError(null);

      await MySwal.fire({
        title: <strong>Element14 Order Success!</strong>,
        html: (
          <i>
            Order created successfully, Your order reference is{" "}
            <b>{orderReference || "-"}</b>
          </i>
        ),
        icon: "success",
      });
      navigate("/orders");
    } catch (err) {
      console.log({ err });
      setError(err?.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={modelData.open} onOpenChange={(open) => setModelData({ ...modelData, open })}>
      <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-primary">Element14 Checkout</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-6 py-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="shippingAddress">
                Shipping Address <span className="text-red-500">*</span>
              </Label>
              <Select value={addressId} onValueChange={setAddressId}>
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Shipping Address" />
                </SelectTrigger>
                <SelectContent>
                  {shippingAddresses.map((addr, index) => {
                    const id = String(addr.addressId ?? addr.id ?? index);
                    return (
                      <SelectItem key={id} value={id}>
                        {getAddressLabel(addr) || `Address ${index + 1}`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="shippingMethod">
                Shipping Method <span className="text-red-500">*</span>
              </Label>
              <Select value={shippingMethodId} onValueChange={setShippingMethodId}>
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Shipping Method" />
                </SelectTrigger>
                <SelectContent>
                  {shippingMethods.map((method, index) => {
                    const id = String(method.shippingMethodID ?? method.id ?? index);
                    return (
                      <SelectItem key={id} value={id}>
                        {getMethodLabel(method) || `Method ${index + 1}`}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="poNumber">
                PO Number <span className="text-gray-400 font-normal">(optional)</span>
              </Label>
              <input
                id="poNumber"
                type="text"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                placeholder={`PO-${Date.now()}`}
                className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="currency">Currency</Label>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger className="col-span-3 bg-gray-50 focus-visible:ring-ring focus-visible:ring-0 focus-visible:ring-offset-0">
                  <SelectValue placeholder="Select Currency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD - US Dollar</SelectItem>
                  <SelectItem value="INR">INR - Indian Rupee</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {error && (
            <div className="text-sm mt-3 mb-4 text-red-500">
              Error: <span className="text-red-500">{error}</span>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="secondary"
              className="hover:bg-gray-300 border border-gray-300"
              type="button"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isLoading}>
              Checkout {isLoading && <SamiSpin className="w-4 h-4 animate-spin" />}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Element14Modal;
